import {useEffect, useMemo, useState} from 'react';

import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {useUnmountCancelToken} from '../../../../../srcCommon/helpers/hookHelpers';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {getCompany} from '../../../../store/data/company/companySelectors';
import {getFlatTeammates, getUser} from '../../../../store/data/teammates/teammatesSelectors';
import {useSettingsCustomFields} from '../../../customFields/useCustomFields';
import {useRuleEditorServiceContext} from './useRuleEditorServiceContext';

export function useFetchAllServices(namespace: string) {
  const [inboxesLoaded, setInboxesLoaded] = useState(false);
  const [tagsLoaded, setTagsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const cancelToken = useUnmountCancelToken();
  const company = useAppSelector(getCompany);
  const user = useAppSelector(getUser);
  const teammates = useAppSelector(getFlatTeammates);
  const {inboxService, tagService} = useRuleEditorServiceContext();

  useEffect(() => {
    // Fetch inboxes, tags and all custom fields so parameters don't have to wait for data
    inboxService
      .prefetch({companyUrl: company.url, namespace, cancelToken})
      .then(() => setInboxesLoaded(true), setError);
    tagService
      .fetch({companyUrl: company.url, namespace, cancelToken, user, teammates})
      .then(() => setTagsLoaded(true), setError);
  }, [namespace, company.url, cancelToken, inboxService, tagService, user, teammates]);

  const {status: customFieldsStatus, error: customFieldsError, ...customFields} = useSettingsCustomFields();

  const status = useMemo(() => {
    if (customFieldsStatus === AsyncStatusesEnum.FAILED) {
      return AsyncStatusesEnum.FAILED;
    }

    if (!inboxesLoaded || !tagsLoaded) {
      return AsyncStatusesEnum.LOADING;
    }

    return customFieldsStatus;
  }, [customFieldsStatus, inboxesLoaded, tagsLoaded]);

  return {
    error: error ?? customFieldsError,
    status,
    teammateCustomFields: customFields.teammate,
    inboxCustomFields: customFields.inbox,
    accountCustomFields: customFields.account,
    contactCustomFields: customFields.card,
  };
}
